import { useState, useEffect } from 'react';
import {
  Route as RouteIcon,
  Plus,
  Edit,
  Trash2,
  Filter,
  RefreshCw,
  ToggleLeft,
  ToggleRight,
  Loader2,
  ChevronLeft,
  ChevronRight,
  CheckSquare,
  Square,
  DollarSign,
  Clock,
  MapPin,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useRegionStore } from '@/stores/regionStore';
import { servicesAPI } from '@/api/services';
import { Button, Loading, ErrorMessage, Input } from '@/components/common';
import { RouteForm } from '@/components/common/RouteForm';
import type {
  ServiceRoutesResponse,
  RoutePricingData,
  ServiceRegionRouteWithRegions,
} from '@/types';

/**
 * RouteManagementPage Component
 * Admin page for managing route-based service pricing (winch services)
 */
export const RouteManagementPage = () => {
  const { services, regions, loading: storeLoading, error: storeError, fetchServices, fetchRegions } = useRegionStore();
  
  // Filter and search state
  const [selectedServiceId, setSelectedServiceId] = useState<number | null>(null);
  const [fromRegionId, setFromRegionId] = useState<number | null>(null);
  const [toRegionId, setToRegionId] = useState<number | null>(null);
  const [fromGovernorate, setFromGovernorate] = useState('');
  const [toGovernorate, setToGovernorate] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [availabilityFilter, setAvailabilityFilter] = useState<boolean | null>(null);

  // Routes data
  const [routes, setRoutes] = useState<ServiceRegionRouteWithRegions[]>([]);
  const [routesLoading, setRoutesLoading] = useState(false);
  const [routesSummary, setRoutesSummary] = useState<any>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRoutes, setTotalRoutes] = useState(0);
  const itemsPerPage = 20;

  // Modal states
  const [showRouteForm, setShowRouteForm] = useState(false);
  const [editingRoute, setEditingRoute] = useState<ServiceRegionRouteWithRegions | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Bulk operations
  const [selectedRoutes, setSelectedRoutes] = useState<Set<number>>(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [bulkPrice, setBulkPrice] = useState('');
  const [bulkUpdating, setBulkUpdating] = useState(false);

  // Get route-based services (pricing_model = 'region_to_region')
  const routeBasedServices = services.filter(
    (s) => s.pricingModel === 'region_to_region'
  );

  // Get unique governorates
  const governorates = Array.from(
    new Set(regions.map((r) => r.governorateAr))
  ).sort();

  useEffect(() => {
    fetchServices();
    fetchRegions();
  }, []);

  useEffect(() => {
    if (selectedServiceId) {
      fetchRoutes();
    } else {
      setRoutes([]);
      setRoutesSummary(null);
    }
  }, [
    selectedServiceId,
    fromRegionId,
    toRegionId,
    fromGovernorate,
    toGovernorate,
    minPrice,
    maxPrice,
    availabilityFilter,
    currentPage,
  ]);

  const fetchRoutes = async () => {
    if (!selectedServiceId) return;

    setRoutesLoading(true);
    try {
      const params: any = {
        page: currentPage,
        limit: itemsPerPage,
      };

      if (fromRegionId) params.fromRegionId = fromRegionId;
      if (toRegionId) params.toRegionId = toRegionId;
      if (fromGovernorate) params.fromGovernorate = fromGovernorate;
      if (toGovernorate) params.toGovernorate = toGovernorate;
      if (minPrice) params.minPrice = parseFloat(minPrice);
      if (maxPrice) params.maxPrice = parseFloat(maxPrice);
      if (availabilityFilter !== null) params.isAvailable = availabilityFilter;

      const response = await servicesAPI.getRoutes(selectedServiceId, params);
      const data = response.data.data as ServiceRoutesResponse['data'];

      setRoutes(data.routes as ServiceRegionRouteWithRegions[]);
      setRoutesSummary(data.summary);
      setTotalPages(data.pagination.pages);
      setTotalRoutes(data.pagination.total);
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'فشل تحميل المسارات');
      setRoutes([]);
    } finally {
      setRoutesLoading(false);
    }
  };

  const handleAddRoute = () => {
    setEditingRoute(null);
    setShowRouteForm(true);
  };

  const handleEditRoute = (route: ServiceRegionRouteWithRegions) => {
    setEditingRoute(route);
    setShowRouteForm(true);
  };

  const handleSubmitRoute = async (data: RoutePricingData) => {
    if (!selectedServiceId) return;

    setIsSubmitting(true);
    try {
      await servicesAPI.upsertRoutePricing(selectedServiceId, data);
      toast.success(editingRoute ? 'تم تحديث المسار بنجاح' : 'تم إضافة المسار بنجاح');
      setShowRouteForm(false);
      setEditingRoute(null);
      fetchRoutes();
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'فشل حفظ المسار');
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteRoute = async (route: ServiceRegionRouteWithRegions) => {
    if (!selectedServiceId) return;

    if (
      !window.confirm(
        `هل أنت متأكد من حذف المسار من "${route.fromRegion.nameAr}" إلى "${route.toRegion.nameAr}"؟`
      )
    ) {
      return;
    }

    try {
      await servicesAPI.deleteRoute(selectedServiceId, route.fromRegionId, route.toRegionId);
      toast.success('تم حذف المسار بنجاح');
      fetchRoutes();
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'فشل حذف المسار');
    }
  };

  const handleToggleAvailability = async (route: ServiceRegionRouteWithRegions) => {
    if (!selectedServiceId) return;

    try {
      await servicesAPI.toggleRouteAvailability(
        selectedServiceId,
        route.fromRegionId,
        route.toRegionId
      );
      toast.success('تم تحديث حالة المسار بنجاح');
      fetchRoutes();
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'فشل تحديث حالة المسار');
    }
  };

  const handleSelectRoute = (routeId: number) => {
    setSelectedRoutes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(routeId)) {
        newSet.delete(routeId);
      } else {
        newSet.add(routeId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedRoutes.size === routes.length) {
      setSelectedRoutes(new Set());
    } else {
      setSelectedRoutes(new Set(routes.map((r) => r.id)));
    }
  };

  const handleBulkUpdatePrice = async () => {
    if (!selectedServiceId || selectedRoutes.size === 0 || !bulkPrice) {
      toast.error('يرجى اختيار مسارات وإدخال سعر');
      return;
    }

    const price = parseFloat(bulkPrice);
    if (isNaN(price) || price <= 0) {
      toast.error('يرجى إدخال سعر صحيح');
      return;
    }

    if (
      !window.confirm(
        `هل أنت متأكد من تحديث ${selectedRoutes.size} مسار إلى ${price.toFixed(3)} د.ك؟`
      )
    ) {
      return;
    }

    setBulkUpdating(true);
    try {
      const selectedRoutesData = routes.filter((r) => selectedRoutes.has(r.id));
      const bulkData = selectedRoutesData.map((route) => ({
        fromRegionId: route.fromRegionId,
        toRegionId: route.toRegionId,
        price,
        isAvailable: route.isAvailable,
      }));

      await servicesAPI.bulkUpdateRoutes(selectedServiceId, bulkData);
      toast.success(`تم تحديث ${selectedRoutes.size} مسار بنجاح`);
      setSelectedRoutes(new Set());
      setBulkPrice('');
      setShowBulkActions(false);
      fetchRoutes();
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'فشل التحديث الشامل');
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleBulkToggleAvailability = async (isAvailable: boolean) => {
    if (!selectedServiceId || selectedRoutes.size === 0) {
      toast.error('يرجى اختيار مسارات أولاً');
      return;
    }

    if (
      !window.confirm(
        `هل أنت متأكد من ${isAvailable ? 'تفعيل' : 'تعطيل'} ${selectedRoutes.size} مسار؟`
      )
    ) {
      return;
    }

    setBulkUpdating(true);
    try {
      const selectedRoutesData = routes.filter((r) => selectedRoutes.has(r.id));
      const bulkData = selectedRoutesData.map((route) => ({
        fromRegionId: route.fromRegionId,
        toRegionId: route.toRegionId,
        price: route.price,
        isAvailable,
      }));

      await servicesAPI.bulkUpdateRoutes(selectedServiceId, bulkData);
      toast.success(`تم ${isAvailable ? 'تفعيل' : 'تعطيل'} ${selectedRoutes.size} مسار بنجاح`);
      setSelectedRoutes(new Set());
      setShowBulkActions(false);
      fetchRoutes();
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'فشل التحديث الشامل');
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedServiceId || selectedRoutes.size === 0) {
      toast.error('يرجى اختيار مسارات أولاً');
      return;
    }

    if (
      !window.confirm(
        `هل أنت متأكد من حذف ${selectedRoutes.size} مسار؟ هذا الإجراء لا يمكن التراجع عنه.`
      )
    ) {
      return;
    }

    setBulkUpdating(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      const selectedRoutesData = routes.filter((r) => selectedRoutes.has(r.id));
      
      for (const route of selectedRoutesData) {
        try {
          await servicesAPI.deleteRoute(
            selectedServiceId,
            route.fromRegionId,
            route.toRegionId
          );
          successCount++;
        } catch {
          errorCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`تم حذف ${successCount} مسار بنجاح`);
      }
      if (errorCount > 0) {
        toast.error(`فشل حذف ${errorCount} مسار`);
      }

      setSelectedRoutes(new Set());
      setShowBulkActions(false);
      fetchRoutes();
    } catch (err: any) {
      toast.error('فشل الحذف الشامل');
    } finally {
      setBulkUpdating(false);
    }
  };

  const clearFilters = () => {
    setFromRegionId(null);
    setToRegionId(null);
    setFromGovernorate('');
    setToGovernorate('');
    setMinPrice('');
    setMaxPrice('');
    setAvailabilityFilter(null);
    setCurrentPage(1);
  };

  if (storeLoading && services.length === 0) {
    return <Loading text="جاري تحميل البيانات..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <RouteIcon className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-primary">إدارة المسارات</h1>
            <p className="text-sm text-gray-400">إدارة أسعار خدمات النقل من منطقة إلى أخرى</p>
          </div>
        </div>
        <div className="flex gap-2">
          {selectedServiceId && (
            <Button onClick={handleAddRoute}>
              <Plus className="h-4 w-4 ml-2" />
              إضافة مسار
            </Button>
          )}
          <Button variant="secondary" onClick={() => { fetchServices(); fetchRegions(); }}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {storeError && <ErrorMessage message={storeError} />}

      {/* Service Selector */}
      <div className="card">
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            اختر الخدمة *
          </label>
          <select
            value={selectedServiceId || ''}
            onChange={(e) => {
              setSelectedServiceId(e.target.value ? parseInt(e.target.value) : null);
              setCurrentPage(1);
              setSelectedRoutes(new Set());
            }}
            className="w-full bg-dark-lighter border border-gray-700 rounded-lg px-4 py-2.5 text-gray-200 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
          >
            <option value="">اختر خدمة...</option>
            {routeBasedServices.map((service) => (
              <option key={service.id} value={service.id}>
                {service.nameAr} ({service.nameEn})
              </option>
            ))}
          </select>
          {routeBasedServices.length === 0 && (
            <p className="mt-2 text-sm text-yellow-500">
              لا توجد خدمات بنظام تسعير المسارات. يرجى إضافة خدمة جديدة بنموذج تسعير "region_to_region".
            </p>
          )}
        </div>
      </div>

      {/* Filters */}
      {selectedServiceId && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-primary flex items-center gap-2">
              <Filter className="h-5 w-5" />
              البحث والتصفية
            </h3>
            <button
              onClick={clearFilters}
              className="text-sm text-gray-400 hover:text-primary transition-colors"
            >
              مسح الفلاتر
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* From Governorate */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                محافظة البداية
              </label>
              <select
                value={fromGovernorate}
                onChange={(e) => {
                  setFromGovernorate(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full bg-dark-lighter border border-gray-700 rounded-lg px-4 py-2 text-gray-200 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
              >
                <option value="">جميع المحافظات</option>
                {governorates.map((gov) => (
                  <option key={gov} value={gov}>
                    {gov}
                  </option>
                ))}
              </select>
            </div>

            {/* From Region */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                منطقة البداية
              </label>
              <select
                value={fromRegionId || ''}
                onChange={(e) => {
                  setFromRegionId(e.target.value ? parseInt(e.target.value) : null);
                  setCurrentPage(1);
                }}
                className="w-full bg-dark-lighter border border-gray-700 rounded-lg px-4 py-2 text-gray-200 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
              >
                <option value="">جميع المناطق</option>
                {regions
                  .filter((r) => !fromGovernorate || r.governorateAr === fromGovernorate)
                  .map((region) => (
                    <option key={region.id} value={region.id}>
                      {region.nameAr}
                    </option>
                  ))}
              </select>
            </div>

            {/* To Governorate */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                محافظة الوجهة
              </label>
              <select
                value={toGovernorate}
                onChange={(e) => {
                  setToGovernorate(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full bg-dark-lighter border border-gray-700 rounded-lg px-4 py-2 text-gray-200 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
              >
                <option value="">جميع المحافظات</option>
                {governorates.map((gov) => (
                  <option key={gov} value={gov}>
                    {gov}
                  </option>
                ))}
              </select>
            </div>

            {/* To Region */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                منطقة الوجهة
              </label>
              <select
                value={toRegionId || ''}
                onChange={(e) => {
                  setToRegionId(e.target.value ? parseInt(e.target.value) : null);
                  setCurrentPage(1);
                }}
                className="w-full bg-dark-lighter border border-gray-700 rounded-lg px-4 py-2 text-gray-200 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
              >
                <option value="">جميع المناطق</option>
                {regions
                  .filter((r) => !toGovernorate || r.governorateAr === toGovernorate)
                  .map((region) => (
                    <option key={region.id} value={region.id}>
                      {region.nameAr}
                    </option>
                  ))}
              </select>
            </div>

            {/* Min Price */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                أقل سعر (د.ك)
              </label>
              <Input
                type="number"
                step="0.001"
                min="0"
                value={minPrice}
                onChange={(e) => {
                  setMinPrice(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="0.000"
              />
            </div>

            {/* Max Price */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                أعلى سعر (د.ك)
              </label>
              <Input
                type="number"
                step="0.001"
                min="0"
                value={maxPrice}
                onChange={(e) => {
                  setMaxPrice(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="0.000"
              />
            </div>

            {/* Availability */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                الحالة
              </label>
              <select
                value={availabilityFilter === null ? '' : availabilityFilter.toString()}
                onChange={(e) => {
                  setAvailabilityFilter(
                    e.target.value === '' ? null : e.target.value === 'true'
                  );
                  setCurrentPage(1);
                }}
                className="w-full bg-dark-lighter border border-gray-700 rounded-lg px-4 py-2 text-gray-200 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
              >
                <option value="">الكل</option>
                <option value="true">متاح</option>
                <option value="false">غير متاح</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Summary Statistics */}
      {selectedServiceId && routesSummary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="card">
            <div className="text-sm text-gray-400">إجمالي المسارات</div>
            <div className="text-2xl font-bold text-primary">{routesSummary.totalRoutes}</div>
          </div>
          <div className="card">
            <div className="text-sm text-gray-400">المسارات المتاحة</div>
            <div className="text-2xl font-bold text-green-500">{routesSummary.availableRoutes}</div>
          </div>
          {routesSummary.priceRange && (
            <>
              <div className="card">
                <div className="text-sm text-gray-400">أقل سعر</div>
                <div className="text-2xl font-bold text-blue-500">
                  {routesSummary.priceRange.min.toFixed(3)} د.ك
                </div>
              </div>
              <div className="card">
                <div className="text-sm text-gray-400">أعلى سعر</div>
                <div className="text-2xl font-bold text-yellow-500">
                  {routesSummary.priceRange.max.toFixed(3)} د.ك
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Bulk Actions */}
      {selectedServiceId && selectedRoutes.size > 0 && (
        <div className="card bg-primary/10 border-primary/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckSquare className="h-5 w-5 text-primary" />
              <span className="text-primary font-medium">
                تم اختيار {selectedRoutes.size} مسار
              </span>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => setShowBulkActions(!showBulkActions)}
              >
                عمليات شاملة
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setSelectedRoutes(new Set())}
              >
                إلغاء التحديد
              </Button>
            </div>
          </div>

          {showBulkActions && (
            <div className="mt-4 pt-4 border-t border-gray-700 space-y-4">
              {/* Bulk Price Update */}
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    تحديث السعر لجميع المسارات المحددة
                  </label>
                  <Input
                    type="number"
                    step="0.001"
                    min="0"
                    value={bulkPrice}
                    onChange={(e) => setBulkPrice(e.target.value)}
                    placeholder="0.000"
                    disabled={bulkUpdating}
                  />
                </div>
                <Button
                  onClick={handleBulkUpdatePrice}
                  disabled={!bulkPrice || bulkUpdating}
                >
                  {bulkUpdating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <DollarSign className="h-4 w-4 ml-2" />
                      تحديث الأسعار
                    </>
                  )}
                </Button>
              </div>

              {/* Bulk Availability Toggle */}
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  onClick={() => handleBulkToggleAvailability(true)}
                  disabled={bulkUpdating}
                >
                  {bulkUpdating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <ToggleRight className="h-4 w-4 ml-2" />
                      تفعيل الكل
                    </>
                  )}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => handleBulkToggleAvailability(false)}
                  disabled={bulkUpdating}
                >
                  {bulkUpdating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <ToggleLeft className="h-4 w-4 ml-2" />
                      تعطيل الكل
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleBulkDelete}
                  disabled={bulkUpdating}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {bulkUpdating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 ml-2" />
                      حذف الكل
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Routes Table */}
      {selectedServiceId && (
        <div className="card">
          {routesLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="mr-3 text-gray-300">جاري تحميل المسارات...</span>
            </div>
          ) : routes.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th className="w-12">
                        <button onClick={handleSelectAll} className="text-gray-400 hover:text-primary">
                          {selectedRoutes.size === routes.length ? (
                            <CheckSquare className="h-5 w-5" />
                          ) : (
                            <Square className="h-5 w-5" />
                          )}
                        </button>
                      </th>
                      <th>من</th>
                      <th>إلى</th>
                      <th>السعر (د.ك)</th>
                      <th>الوقت المتوقع</th>
                      <th>المسافة</th>
                      <th>الحالة</th>
                      <th>الإجراءات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {routes.map((route) => (
                      <tr key={route.id} className={!route.isAvailable ? 'opacity-60' : ''}>
                        <td>
                          <button
                            onClick={() => handleSelectRoute(route.id)}
                            className="text-gray-400 hover:text-primary"
                          >
                            {selectedRoutes.has(route.id) ? (
                              <CheckSquare className="h-5 w-5 text-primary" />
                            ) : (
                              <Square className="h-5 w-5" />
                            )}
                          </button>
                        </td>
                        <td>
                          <div>
                            <div className="font-medium text-gray-200">
                              {route.fromRegion.nameAr}
                            </div>
                            <div className="text-sm text-gray-400">
                              {route.fromRegion.governorateAr}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div>
                            <div className="font-medium text-gray-200">
                              {route.toRegion.nameAr}
                            </div>
                            <div className="text-sm text-gray-400">
                              {route.toRegion.governorateAr}
                            </div>
                          </div>
                        </td>
                        <td className="font-bold text-primary">
                          {route.price.toFixed(3)}
                        </td>
                        <td className="text-gray-400">
                          {route.estimatedTimeMinutes ? (
                            <div className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              {route.estimatedTimeMinutes} دقيقة
                            </div>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="text-gray-400">
                          {route.distanceKm ? (
                            <div className="flex items-center gap-1">
                              <MapPin className="h-4 w-4" />
                              {route.distanceKm} كم
                            </div>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>
                          <span
                            className={`badge ${
                              route.isAvailable ? 'badge-success' : 'badge-error'
                            }`}
                          >
                            {route.isAvailable ? 'متاح' : 'غير متاح'}
                          </span>
                        </td>
                        <td>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditRoute(route)}
                              className="p-2 hover:bg-dark rounded transition-colors"
                              title="تعديل"
                            >
                              <Edit className="h-4 w-4 text-blue-500" />
                            </button>
                            <button
                              onClick={() => handleToggleAvailability(route)}
                              className="p-2 hover:bg-dark rounded transition-colors"
                              title={route.isAvailable ? 'تعطيل' : 'تفعيل'}
                            >
                              {route.isAvailable ? (
                                <ToggleRight className="h-4 w-4 text-green-500" />
                              ) : (
                                <ToggleLeft className="h-4 w-4 text-gray-500" />
                              )}
                            </button>
                            <button
                              onClick={() => handleDeleteRoute(route)}
                              className="p-2 hover:bg-dark rounded transition-colors"
                              title="حذف"
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700">
                  <div className="text-sm text-gray-400">
                    عرض {(currentPage - 1) * itemsPerPage + 1} -{' '}
                    {Math.min(currentPage * itemsPerPage, totalRoutes)} من {totalRoutes}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <div className="flex items-center gap-2 px-3">
                      <span className="text-gray-400">صفحة</span>
                      <span className="text-primary font-medium">{currentPage}</span>
                      <span className="text-gray-400">من {totalPages}</span>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <RouteIcon className="h-16 w-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg mb-2">لا توجد مسارات</p>
              <p className="text-gray-500 text-sm mb-4">
                ابدأ بإضافة مسارات جديدة للخدمة المحددة
              </p>
              <Button onClick={handleAddRoute}>
                <Plus className="h-4 w-4 ml-2" />
                إضافة مسار
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Route Form Modal */}
      {showRouteForm && (
        <RouteForm
          regions={regions}
          initialData={
            editingRoute
              ? {
                  fromRegionId: editingRoute.fromRegionId,
                  toRegionId: editingRoute.toRegionId,
                  price: editingRoute.price,
                  isAvailable: editingRoute.isAvailable,
                  estimatedTimeMinutes: editingRoute.estimatedTimeMinutes,
                  distanceKm: editingRoute.distanceKm,
                  notes: editingRoute.notes,
                  fromRegionName: editingRoute.fromRegion.nameAr,
                  toRegionName: editingRoute.toRegion.nameAr,
                }
              : undefined
          }
          onSubmit={handleSubmitRoute}
          onCancel={() => {
            setShowRouteForm(false);
            setEditingRoute(null);
          }}
          isSubmitting={isSubmitting}
        />
      )}
    </div>
  );
};

export default RouteManagementPage;
